import { expVal } from '@confluence/feature-experiments';

import { useIsSmartFoldersEnabled } from './useIsSmartFoldersEnabled.ts';

type UseIsSmartFoldersEnabledForMilestoneOptions = {
	/**
	 * When `true`, short-circuits the hook so it does not invoke the
	 * underlying `useIsSmartFoldersEnabled` GraphQL query or evaluate the
	 * experiment.  Use this when the caller already knows the result will
	 * not be consumed (e.g. when the content is not a smart folder) to
	 * avoid an unnecessary network round-trip per render.
	 */
	skip?: boolean;
};

type UseIsSmartFoldersEnabledForMilestoneResult = {
	isSmartFoldersEnabledForMilestone: boolean;
	loading: boolean;
};

/**
 * Hook to check whether smart folders are enabled for a specific milestone.
 *
 * This performs all the checks from `useIsSmartFoldersEnabled` and additionally
 * checks the `confluence_smart_folders_milestone` dynamic config value. If the
 * configured milestone value is equal to or greater than the provided milestone
 * parameter, it returns true.
 *
 * @param spaceKey - The space key to check whether folders are enabled for
 * @param milestone - The milestone number to check against the dynamic config value
 * @param options - Optional behaviour controls (see `skip` above)
 */
export const useIsSmartFoldersEnabledForMilestone = (
	spaceKey: string,
	milestone: number,
	options: UseIsSmartFoldersEnabledForMilestoneOptions = {},
): UseIsSmartFoldersEnabledForMilestoneResult => {
	const { skip = false } = options;
	const { isSmartFoldersEnabled, loading } = useIsSmartFoldersEnabled(skip ? null : spaceKey);

	if (skip) {
		return { isSmartFoldersEnabledForMilestone: false, loading: false };
	}

	// eslint-disable-next-line confluence-feature-gating/inline-usage
	return {
		isSmartFoldersEnabledForMilestone:
			isSmartFoldersEnabled &&
			expVal<number>('confluence_smart_folders_milestone', 'value', 0) >= milestone,
		loading,
	};
};
